import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Camera, CheckCircle2, ClipboardList, Loader2, Store, X,
} from 'lucide-react';
// Vídeo do procedimento embutido na tela (componente compartilhado).
// ⚠️ O `apiBase` é o proxy do MÓDULO dono do vídeo — cada módulo tem o seu.
import VideoProcedimento from '../../VideoProcedimento';

// =====================================================================
// AUDITORIA — Registro de Nulos em Progresso: o FORMULÁRIO da loja.
//
// O gerente declara o pedido que foi cancelado no POS antes de a venda fechar,
// com a foto da tela do POS. O portal não impede o cancelamento — ele obriga a
// declaração com evidência.
//
// ⚠️ A TELA NÃO REIMPLEMENTA REGRA DE PERFIL. Quem diz se a pessoa registra, se
//    escolhe a loja e quais são as formas de pagamento é o `GET /opcoes`. Cópia
//    de regra no front envelhece de um lado só — e o sintoma é a pessoa escolher
//    uma opção que o servidor recusa.
//
// ⚠️ A LOJA do gerente NÃO é enviada de propósito: o servidor a lê do cadastro e
//    ignora o corpo. Mandá-la daria a impressão, para quem lê este arquivo, de
//    que a tela decide de quem é o nulo.
// =====================================================================

const API = '/api/financeiro/nulos';
// Proxy de vídeo do módulo Financeiro (catálogo em dtbVideoProcedimento).
const API_VIDEOS = '/api/financeiro/videos';
// Paleta do módulo FINANCEIRO (verde-petróleo). ⚠️ Era o laranja da Auditoria até
// 28/08/2026 — tela dentro de um módulo usa a cor DELE (docs/ARQUITETURA.md ›
// Paleta dos módulos); a fonte da verdade é o `cor=` do ModuloHeader no hub.
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

/** 'HH:MM' de agora — o nulo é declarado logo depois de acontecer. */
const horaAgora = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const brl = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** O usuário do portal, como o login o gravou. Usado só para o RÓTULO da loja. */
const usuarioLogado = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

// --- pedaços visuais reaproveitados dentro do arquivo -----------------

const Campo = ({ label, obrigatorio, dica, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
      {label} {obrigatorio && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    {children}
    {dica && <span style={{ fontSize: 12, color: '#94a3b8' }}>{dica}</span>}
  </div>
);

const estiloInput = {
  padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 15, color: '#0f172a', background: '#fff', width: '100%',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

/**
 * Uma pergunta de Sim/Não em dois botões.
 *
 * ⚠️ NENHUMA opção vem pré-marcada, nas duas perguntas: são justamente os campos
 *    que separam erro de digitação no POS de valor devolvido ao cliente e nunca
 *    recobrado — e um padrão marcado é respondido no automático.
 *
 * ⚠️ `corSim`/`corNao` existem porque a resposta "de risco" TROCA DE LADO entre as
 *    duas perguntas: em "foi estornado?" o vermelho é o **Sim**; em "cobrou
 *    novamente?" é o **Não**. Pintar as duas igual ensinaria a operação a associar
 *    o lado do botão à cor, não à resposta.
 *
 * Botões e não `select`: são duas opções e a tela é usada no celular, onde o toque
 * é mais rápido e menos sujeito a erro que a lista.
 */
const PerguntaSimNao = ({ valor, onEscolher, corSim = 'risco', corNao = 'ok' }) => {
  const PALETA = {
    risco: { cor: '#b91c1c', fundo: '#fee2e2' },
    ok: { cor: '#15803d', fundo: '#dcfce7' },
  };
  const opcoes = [
    { v: 'sim', rotulo: 'Sim', ...PALETA[corSim] },
    { v: 'nao', rotulo: 'Não', ...PALETA[corNao] },
  ];
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {opcoes.map((o) => {
        const ativo = valor === o.v;
        return (
          <button key={o.v} type="button" onClick={() => onEscolher(o.v)} style={{
            padding: '10px 28px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
            border: ativo ? `2px solid ${o.cor}` : '1px solid #cbd5e1',
            background: ativo ? o.fundo : '#fff',
            color: ativo ? o.cor : '#475569',
          }}>{o.rotulo}</button>
        );
      })}
    </div>
  );
};

const NulosRegistro = ({ onIrParaControle }) => {
  const [opcoes, setOpcoes] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [gerentes, setGerentes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [gravado, setGravado] = useState(null);

  const [form, setForm] = useState({
    data: '', hora: horaAgora(), loja: '', gerenteId: '',
    pos: '', formaPagamento: '', valor: '', estornado: '', cobradoNovamente: '',
  });
  const [foto, setFoto] = useState(null);
  const [previa, setPrevia] = useState('');

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  // --- carga inicial: opções + lojas ---------------------------------
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const rOpcoes = await fetch(`${API}/opcoes`, { headers: auth() });
        if (!rOpcoes.ok) throw new Error('opcoes');
        const o = await rOpcoes.json();
        if (!vivo) return;
        setOpcoes(o);

        // A lista de lojas só é pedida a quem ESCOLHE a loja.
        // ⚠️⚠️ Para o gerente, a loja sai do CADASTRO (o token, no localStorage) e
        //    NÃO da lista de lojas — que vem da `dtbCadastroLojas`. A LIN não está
        //    lá (emite fora da nossa licença Sovos), então usar a lista deixaria o
        //    gerente de Linhares com o campo vazio, o seletor de gerente desligado
        //    e nenhuma explicação na tela. É rótulo: quem decide de quem é o nulo
        //    é o servidor, que ignora este valor.
        let ls = [];
        if (o.escolheLoja) {
          const rLojas = await fetch(`${API}/lojas`, { headers: auth() });
          ls = rLojas.ok ? await rLojas.json() : [];
          if (!vivo) return;
          setLojas(ls);
        }

        setForm((f) => ({
          ...f,
          data: o.hoje || '',
          loja: o.escolheLoja ? '' : String(usuarioLogado()?.loja || '').trim(),
        }));
      } catch {
        if (vivo) setErro('Não foi possível carregar o formulário. Recarregue a página.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  // --- os gerentes daquela loja --------------------------------------
  const carregarGerentes = useCallback(async (loja) => {
    if (!loja) { setGerentes([]); return; }
    try {
      const r = await fetch(`${API}/gerentes?loja=${encodeURIComponent(loja)}`, { headers: auth() });
      setGerentes(r.ok ? await r.json() : []);
    } catch {
      setGerentes([]);
    }
  }, []);

  useEffect(() => { carregarGerentes(form.loja); }, [form.loja, carregarGerentes]);

  // ⚠️ TROCAR DE LOJA LIMPA O GERENTE. Sem isso o gerente da loja anterior
  //    seguiria escolhido e o `POST` recusaria ("não é da loja X") num formulário
  //    que parece certo — o mesmo cuidado que a abertura de chamado tomou em 28/08.
  const trocarLoja = (loja) => setForm((f) => ({ ...f, loja, gerenteId: '' }));

  // --- a evidência ---------------------------------------------------
  const escolherFoto = (arquivo) => {
    if (previa) URL.revokeObjectURL(previa);
    setFoto(arquivo || null);
    setPrevia(arquivo ? URL.createObjectURL(arquivo) : '');
  };
  // Revoga o objectURL ao desmontar — senão vaza memória.
  useEffect(() => () => { if (previa) URL.revokeObjectURL(previa); }, [previa]);

  // --- envio ---------------------------------------------------------
  const enviar = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      // ⚠️ `FormData` porque a foto viaja NA MESMA requisição do registro: é isso
      //    que garante que não existe registro sem evidência.
      const fd = new FormData();
      fd.append('data', form.data);
      fd.append('hora', form.hora);
      if (opcoes?.escolheLoja) fd.append('loja', form.loja);
      fd.append('gerenteId', form.gerenteId);
      fd.append('pos', form.pos);
      fd.append('formaPagamento', form.formaPagamento);
      fd.append('valor', form.valor);
      fd.append('estornado', form.estornado);
      fd.append('cobradoNovamente', form.cobradoNovamente);
      if (foto) fd.append('foto', foto);

      const r = await fetch(API, { method: 'POST', headers: auth(), body: fd });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível registrar o nulo.');

      const gerente = gerentes.find((g) => String(g.ID) === String(form.gerenteId));
      setGravado({ ...corpo, gerenteNome: corpo.gerenteNome || gerente?.NOME || '' });
    } catch (ex) {
      setErro(ex.message);
    } finally {
      setEnviando(false);
    }
  };

  const novoRegistro = () => {
    setGravado(null);
    escolherFoto(null);
    setForm((f) => ({
      // Preserva data e loja: quem declara vários nulos do mesmo turno não
      // reescolhe isso a cada um. POS, valor, forma e gerente zeram — repetir
      // esses é justamente o que produziria registro duplicado sem ninguém notar.
      ...f, hora: horaAgora(), gerenteId: '', pos: '', formaPagamento: '', valor: '',
      estornado: '', cobradoNovamente: '',
    }));
  };

  // =====================================================================
  if (carregando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
        <Loader2 size={18} className="spin" /> Carregando o formulário…
      </div>
    );
  }

  if (!opcoes?.podeRegistrar) {
    return (
      <div style={{ padding: 24, background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
        Seu perfil consulta os registros de nulos, mas não abre novos.
      </div>
    );
  }

  // --- confirmação ---------------------------------------------------
  // ⚠️ Tela própria em vez de um alerta: o registro é definitivo (só o Financeiro
  //    ou a Auditoria cancelam), então a pessoa precisa VER o que ficou gravado.
  if (gravado) {
    const linha = (rotulo, valor) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ color: '#64748b', fontSize: 14 }}>{rotulo}</span>
        <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>{valor}</span>
      </div>
    );
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', padding: 16, background: '#dcfce7', borderRadius: '50%', marginBottom: 12 }}>
            <CheckCircle2 size={36} color="#16a34a" />
          </div>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Nulo registrado</h3>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            Registro nº <strong style={{ color: COR_FORTE }}>{gravado.id}</strong> — o Financeiro e a Auditoria já enxergam este lançamento.
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          {linha('Loja', gravado.loja)}
          {linha('Data e hora', `${(gravado.data || '').split('-').reverse().join('/')} às ${String(gravado.hora || '').slice(0, 5)}`)}
          {linha('POS', gravado.pos)}
          {linha('Gerente responsável', gravado.gerenteNome || '—')}
          {linha('Forma de pagamento', gravado.formaPagamento)}
          {linha('Valor', <span style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(gravado.valor)}</span>)}
          {linha('Estornado para o cliente', gravado.estornado === 1
            ? <span style={{ color: '#b91c1c' }}>Sim</span>
            : <span style={{ color: '#15803d' }}>Não</span>)}
          {/* ⚠️ As duas se leem em PAR: estornado SIM + cobrado NÃO é o valor que
              voltou ao cliente e nunca foi recobrado. Daí aparecerem lado a lado. */}
          {linha('Cobrado novamente do cliente', gravado.cobradoNovamente === 1
            ? <span style={{ color: '#15803d' }}>Sim</span>
            : <span style={{ color: '#b91c1c' }}>Não</span>)}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={novoRegistro} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
            background: COR, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}>
            <ClipboardList size={18} /> Registrar outro nulo
          </button>
          {onIrParaControle && (
            <button onClick={onIrParaControle} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
              background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`,
              borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}>
              Ver o controle de nulos
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- formulário ----------------------------------------------------
  return (
    <form onSubmit={enviar} style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Registro de Nulos em Progresso</h3>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
          Declare o pedido cancelado no POS antes de a venda fechar. O registro é definitivo —
          correção é pedida ao Financeiro.
        </p>
        {/* O "como fazer" ao lado do próprio formulário: o registro é definitivo,
            então quem tem dúvida do procedimento precisa resolvê-la ANTES de
            enviar, sem sair da tela. Some sozinho se o vídeo não estiver no ar. */}
        <div style={{ marginTop: 12 }}>
          <VideoProcedimento apiBase={API_VIDEOS} chave="nulos-registro" cor={COR} />
        </div>
      </div>

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, marginBottom: 20,
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14,
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{erro}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginBottom: 18 }}>
        <Campo label="Data" obrigatorio>
          {/* ⚠️ `max` = hoje: a data no futuro é recusada pelo servidor, e barrar
              aqui evita a viagem. Retroativo segue livre (turno declarado no dia
              seguinte é o caso comum). */}
          <input type="date" required value={form.data} max={opcoes.hoje}
            onChange={(e) => set('data', e.target.value)} style={estiloInput} />
        </Campo>

        <Campo label="Hora" obrigatorio>
          <input type="time" required value={form.hora}
            onChange={(e) => set('hora', e.target.value)} style={estiloInput} />
        </Campo>

        <Campo label="Loja" obrigatorio>
          {opcoes.escolheLoja ? (
            <select required value={form.loja} onChange={(e) => trocarLoja(e.target.value)} style={estiloInput}>
              <option value="">Escolha a loja…</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          ) : (
            /* Para o gerente a loja não é escolha — é quem ele é. Mostrada como
               carimbo, e nem enviada ao servidor (ele a lê do cadastro). */
            <div style={{
              ...estiloInput, display: 'flex', alignItems: 'center', gap: 8,
              background: '#f8fafc', color: '#334155', fontWeight: 700,
            }}>
              <Store size={16} color={COR} /> {form.loja || '—'}
            </div>
          )}
        </Campo>

        <Campo label="Gerente responsável" obrigatorio
          dica={form.loja && gerentes.length === 0 ? 'Nenhum gerente ativo cadastrado nesta loja — avise o Admin.' : ''}>
          {/* ⚠️ LISTA, nunca texto livre: nome digitado vira 'joão'/'gerente' e
              nenhum relatório lê. A lista sai da `dtbUsuariosSite` pela API. */}
          <select required value={form.gerenteId} onChange={(e) => set('gerenteId', e.target.value)}
            disabled={!form.loja} style={{ ...estiloInput, background: form.loja ? '#fff' : '#f1f5f9' }}>
            <option value="">{form.loja ? 'Escolha o gerente…' : 'Escolha a loja primeiro'}</option>
            {gerentes.map((g) => (
              <option key={g.ID} value={g.ID}>
                {g.NOME}{g.PERFIL === 'Gerente Plantao' ? ' (plantão)' : ''}
              </option>
            ))}
          </select>
        </Campo>

        <Campo label="POS" obrigatorio dica="O número do terminal (ex.: 3, 41).">
          <input required value={form.pos} onChange={(e) => set('pos', e.target.value)}
            placeholder="3" inputMode="numeric" maxLength={10} style={estiloInput} />
        </Campo>

        <Campo label="Valor" obrigatorio
          dica={form.valor ? brl(String(form.valor).replace(/\./g, '').replace(',', '.')) : 'Use vírgula para os centavos.'}>
          <input required value={form.valor} onChange={(e) => set('valor', e.target.value)}
            placeholder="12,50" inputMode="decimal"
            style={{ ...estiloInput, fontVariantNumeric: 'tabular-nums' }} />
        </Campo>
      </div>

      <div style={{ marginBottom: 18 }}>
        <Campo label="Forma de pagamento" obrigatorio>
          {/* Botões e não `select`: são poucas opções e a tela é usada no celular,
              onde o toque é mais rápido e menos sujeito a erro que a lista.
              ⚠️ A lista vem do `GET /opcoes` e o `flexWrap` já quebra em duas
              linhas — acrescentar forma nova no backend não pede nada aqui. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {opcoes.formasPagamento.map((f) => {
              const ativo = form.formaPagamento === f;
              return (
                <button key={f} type="button" onClick={() => set('formaPagamento', f)} style={{
                  padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  border: ativo ? `2px solid ${COR}` : '1px solid #cbd5e1',
                  background: ativo ? COR_FUNDO : '#fff',
                  color: ativo ? COR_FORTE : '#475569',
                }}>{f}</button>
              );
            })}
          </div>
        </Campo>
      </div>

      <div style={{ marginBottom: 22 }}>
        {/* ⚠️⚠️ AS DUAS PERGUNTAS FICAM LADO A LADO porque é o PAR delas que a
            auditoria lê: estornado SIM + cobrado NÃO = o valor voltou ao cliente e
            nunca foi recobrado. Separá-las esconderia a relação de quem preenche.
            ⚠️ E são INDEPENDENTES: não há pergunta condicional aqui, porque "não
            entornou" também admite ter sido cobrado num pedido novo. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
          <Campo label="Foi estornado para o cliente?" obrigatorio>
            <PerguntaSimNao valor={form.estornado} onEscolher={(v) => set('estornado', v)} />
          </Campo>

          {/* ⚠️ Aqui o vermelho é o **Não**: quem não cobrou de novo é o caso de
              risco. Ver a nota de `corSim`/`corNao` no componente. */}
          <Campo label="Cobrado novamente o valor do cliente?" obrigatorio
            dica={form.estornado === 'sim' && form.cobradoNovamente === 'nao'
              ? '⚠️ Valor estornado e não recobrado — este registro entra no painel de risco do Financeiro.'
              : ''}>
            <PerguntaSimNao valor={form.cobradoNovamente} corSim="ok" corNao="risco"
              onEscolher={(v) => set('cobradoNovamente', v)} />
          </Campo>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Campo label="Foto da tela do POS" obrigatorio
          dica="Obrigatória: é a evidência do pedido cancelado. Máximo 12 MB.">
          {previa ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <img src={previa} alt="Prévia da foto do POS" style={{
                width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0',
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {foto?.name}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {(foto?.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button type="button" onClick={() => escolherFoto(null)} style={{
                  marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', background: '#fee2e2', color: '#991b1b',
                  border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>
                  <X size={14} /> Trocar foto
                </button>
              </div>
            </div>
          ) : (
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              padding: '28px 16px', border: `2px dashed ${COR_BORDA}`, borderRadius: 10,
              background: COR_FUNDO, cursor: 'pointer', color: COR_FORTE,
            }}>
              <Camera size={26} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>Tirar foto ou escolher imagem</span>
              {/* `capture="environment"` abre a câmera de trás direto no celular. */}
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={(e) => escolherFoto(e.target.files?.[0] || null)} />
            </label>
          )}
        </Campo>
      </div>

      <button type="submit" disabled={enviando} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: '100%', padding: '14px 24px', background: enviando ? '#cbd5e1' : COR,
        color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 16,
        cursor: enviando ? 'default' : 'pointer',
      }}>
        {enviando ? <><Loader2 size={18} className="spin" /> Registrando…</> : <>Registrar nulo</>}
      </button>
    </form>
  );
};

export default NulosRegistro;
